import React, { useEffect, useState, createElement } from 'react';
import { Box, Chip, TextField, InputAdornment, FormControl, Select, MenuItem, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from './ui/ToastProvider';
import { VALID_MTOS, VALID_SUB_AGENTS, type ValidMTO, type ValidSubAgent } from '../utils/constants';
import { transactionStore } from '../utils/transactionStore';
import AmendTransactionDialog from './AmendTransactionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { canAmendTransaction, canCancelTransaction, getCancellationAction } from '../utils/transactionRules';
// Types
type PayoutType = 'FT' | 'IBFT' | 'OTC' | 'P2P' | 'B2B' | 'B2C';
type LifecycleStatus = 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'IN_REVIEW' | 'PENDING_INQUIRY';
type CurrentState = 'SCREENING' | 'TITLE_FETCH' | 'BALANCE_CHECK' | 'FT_POST' | 'RAAST_TITLE_FETCH' | 'RAAST_POST' | 'ONELINK_TITLE_FETCH' | 'ONELINK_POST' | 'RTGS_BALANCE_CHECK' | 'RTGS_POST' | 'SUCCESS';
type StateStatus = 'PASS' | 'FAIL' | 'PENDING' | 'REVIEW';
interface StateHistoryEntry {
  stateCode: CurrentState;
  status: StateStatus;
  timestamp: string;
  failReasonCode?: string;
  primaryRef?: string;
  requestSnippet?: string;
  responseSnippet?: string;
  durationMs?: number;
  attempts?: number;
}
export interface Transaction {
  id: string;
  mtoName: string;
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  payoutType: PayoutType;
  region: 'Ghana' | 'Nigeria' | 'Ivory Coast' | 'Senegal' | 'Other';
  transactionCategory: 'P2P' | 'B2B' | 'B2C';
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  corridor: string;
  purposeCode: string;
  purposeText: string;
  currentState: CurrentState;
  lifecycleStatus: LifecycleStatus;
  createdAt: string;
  updatedAt: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  beneficiaryBankName?: string;
  railRefs: {
    RRN?: string;
    STAN?: string;
    RaastMsgId?: string;
    UETR?: string;
  };
  ePRCNo?: string;
  ePRCDate?: string;
  stateHistory: StateHistoryEntry[];
  amendAllowed?: boolean;
  cancelAllowed?: boolean;
  amendStatus?: string;
  cancelStatus?: string;
  amendments?: {
    id: string;
    timestamp: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    reason: string;
    changes: {
      field: string;
      oldValue: string;
      newValue: string;
    }[];
  }[];
}
const MTO_NAMES = ['RIA Money Transfer', 'InstantCash', 'MoneyGram', 'Western Union', 'Xpress Money'];
const AGENT_CODES = ['Bank of AJ&K', 'BIPL', 'FBL', 'HBL', 'UBL', 'MCB', 'ABL'];
const BRANCH_CODES = ['0001', '0002', '0003', '0004', '0005', '0006', '0007', '0008'];
const MOCK_TRANSACTIONS: Transaction[] = Array(50).fill(null).map((_, index) => {
  // Distribute transaction types: OTC, FT, IBFT, P2P, B2B, B2C
  let payoutType: PayoutType;
  if (index % 6 === 0) payoutType = 'OTC';else if (index % 6 === 1) payoutType = 'FT';else if (index % 6 === 2) payoutType = 'IBFT';else if (index % 6 === 3) payoutType = 'P2P';else if (index % 6 === 4) payoutType = 'B2B';else payoutType = 'B2C';
  const regions: ('Ghana' | 'Nigeria' | 'Ivory Coast' | 'Senegal' | 'Other')[] = ['Ghana', 'Nigeria', 'Ivory Coast', 'Senegal', 'Other'];
  const region = regions[index % regions.length];
  const categories: ('P2P' | 'B2B' | 'B2C')[] = ['P2P', 'B2B', 'B2C'];
  const transactionCategory = categories[index % categories.length];
  const lifecycleOptions: LifecycleStatus[] = ['IN_PROGRESS', 'SUCCESS', 'FAILED', 'IN_REVIEW', 'PENDING_INQUIRY'];
  const lifecycle = lifecycleOptions[index % 5];
  let currentState: CurrentState;
  let stateHistory: StateHistoryEntry[] = [];
  const baseTime = Date.now() - index * 3600000;
  // Determine amendAllowed based on state and lifecycle
  let amendAllowed = false;
  let cancelAllowed = false;
  // Build state history based on lifecycle
  if (lifecycle === 'SUCCESS') {
    currentState = 'SUCCESS';
    amendAllowed = false;
    cancelAllowed = false;
    if (payoutType === 'FT') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 4000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `TF-${index}`,
        requestSnippet: `{"accountNo":"${String(index).padStart(16, '0')}"}`,
        responseSnippet: `{"title":"${String(index).padStart(16, '0')}","status":"ACTIVE"}`,
        durationMs: 800,
        attempts: 1
      }, {
        stateCode: 'BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `BC-${index}`,
        durationMs: 500,
        attempts: 1
      }, {
        stateCode: 'FT_POST',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `RRN${String(index).padStart(12, '0')}`,
        requestSnippet: `{"amount":"${(500 + index * 10) * 279}","currency":"PKR"}`,
        responseSnippet: `{"rrn":"RRN${String(index).padStart(12, '0')}","stan":"${String(index).padStart(6, '0')}"}`,
        durationMs: 2300,
        attempts: 1
      }, {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `EPRC-${index}`,
        durationMs: 100,
        attempts: 1
      }];
    } else {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 4000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `RAAST-TF-${index}`,
        requestSnippet: `{"iban":"PK${String(index).padStart(22, '0')}"}`,
        responseSnippet: `{"title":"Beneficiary ${index + 1}","status":"ACTIVE"}`,
        durationMs: 1500,
        attempts: 1
      }, {
        stateCode: 'RAAST_POST',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `RAAST${index}`,
        requestSnippet: `{"amount":"${(500 + index * 10) * 279}","currency":"PKR"}`,
        responseSnippet: `{"raastMsgId":"RAAST${index}","uetr":"UETR-${index}"}`,
        durationMs: 3200,
        attempts: 1
      }, {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `EPRC-${index}`,
        durationMs: 100,
        attempts: 1
      }];
    }
  } else if (lifecycle === 'IN_REVIEW') {
    currentState = 'SCREENING';
    amendAllowed = true;
    cancelAllowed = true;
    stateHistory = [{
      stateCode: 'SCREENING',
      status: 'REVIEW',
      timestamp: new Date(baseTime).toISOString(),
      primaryRef: `SCR-${index}`,
      failReasonCode: 'MANUAL_REVIEW_REQUIRED',
      requestSnippet: `{"remitter":"Remitter ${index + 1}","beneficiary":"Beneficiary ${index + 1}"}`,
      durationMs: 1500,
      attempts: 1
    }];
  } else if (lifecycle === 'PENDING_INQUIRY') {
    currentState = payoutType === 'IBFT' ? 'RAAST_POST' : 'FT_POST';
    amendAllowed = false;
    cancelAllowed = true;
    if (payoutType === 'FT') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `TF-${index}`,
        durationMs: 800,
        attempts: 1
      }, {
        stateCode: 'BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `BC-${index}`,
        durationMs: 500,
        attempts: 1
      }, {
        stateCode: 'FT_POST',
        status: 'PENDING',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `FT-${index}`,
        failReasonCode: 'TIMEOUT_INQUIRY_NEEDED',
        durationMs: 30000,
        attempts: 2
      }];
    } else {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `RAAST-TF-${index}`,
        durationMs: 1500,
        attempts: 1
      }, {
        stateCode: 'RAAST_POST',
        status: 'PENDING',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `RAAST-${index}`,
        failReasonCode: 'TIMEOUT_INQUIRY_NEEDED',
        durationMs: 30000,
        attempts: 2
      }];
    }
  } else if (lifecycle === 'FAILED') {
    const failStates: CurrentState[] = payoutType === 'FT' ? ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'] : ['RAAST_TITLE_FETCH', 'ONELINK_POST', 'RTGS_POST'];
    currentState = failStates[index % failStates.length];
    // Amend allowed for early stage failures
    amendAllowed = ['TITLE_FETCH', 'SCREENING', 'BALANCE_CHECK', 'RAAST_TITLE_FETCH', 'ONELINK_TITLE_FETCH'].includes(currentState);
    cancelAllowed = true;
    if (currentState === 'TITLE_FETCH') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `TF-${index}`,
        failReasonCode: 'INVALID_ACCOUNT',
        requestSnippet: `{"accountNo":"${String(index).padStart(16, '0')}"}`,
        responseSnippet: `{"error":"ACCOUNT_NOT_FOUND"}`,
        durationMs: 1200,
        attempts: 3
      }];
    } else if (currentState === 'BALANCE_CHECK') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `TF-${index}`,
        durationMs: 800,
        attempts: 1
      }, {
        stateCode: 'BALANCE_CHECK',
        status: 'FAIL',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `BC-${index}`,
        failReasonCode: 'INSUFFICIENT_BALANCE',
        durationMs: 500,
        attempts: 1
      }];
    } else {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: currentState,
        status: 'FAIL',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `POST-${index}`,
        failReasonCode: 'POSTING_ERROR',
        durationMs: 3000,
        attempts: 3
      }];
    }
  } else {
    // IN_PROGRESS
    const progressStates: CurrentState[] = payoutType === 'FT' ? ['SCREENING', 'TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'] : ['SCREENING', 'RAAST_TITLE_FETCH', 'RAAST_POST', 'ONELINK_TITLE_FETCH'];
    currentState = progressStates[index % progressStates.length];
    // Amend allowed for early stage in progress
    amendAllowed = ['SCREENING', 'TITLE_FETCH', 'BALANCE_CHECK', 'RAAST_TITLE_FETCH', 'ONELINK_TITLE_FETCH'].includes(currentState);
    cancelAllowed = true;
    stateHistory = [{
      stateCode: 'SCREENING',
      status: currentState === 'SCREENING' ? 'PENDING' : 'PASS',
      timestamp: new Date(baseTime - 2000).toISOString(),
      primaryRef: `SCR-${index}`,
      durationMs: currentState === 'SCREENING' ? undefined : 1200,
      attempts: 1
    }];
    if (currentState !== 'SCREENING') {
      stateHistory.push({
        stateCode: currentState,
        status: 'PENDING',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `${currentState}-${index}`,
        attempts: 1
      });
    }
  }
  return {
    id: `TXN-${10000 + index}`,
    mtoName: index % 10 === 0 ? '' : VALID_MTOS[index % VALID_MTOS.length],
    mtoRef: `MTO-${20000 + index}`,
    bankRef: `BNK-${30000 + index}`,
    // Only assign branch code to OTC transactions
    branchCode: payoutType === 'OTC' ? BRANCH_CODES[index % BRANCH_CODES.length] : '',
    // Only assign agent code to OTC transactions - use validated sub-agents
    agentCode: payoutType === 'OTC' ? VALID_SUB_AGENTS[index % VALID_SUB_AGENTS.length] : '',
    payoutType,
    region,
    transactionCategory,
    amountFCY: `${(500 + index * 10).toFixed(2)}`,
    currencyFCY: 'USD',
    amountPKR: `${((500 + index * 10) * 279).toLocaleString()}`,
    corridor: ['PK-AE', 'PK-SA', 'PK-UK', 'PK-US'][index % 4],
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Family Support', 'Salary', 'Education', 'Medical'][index % 4],
    currentState,
    lifecycleStatus: lifecycle,
    createdAt: new Date(baseTime - 5000).toISOString(),
    updatedAt: new Date(baseTime).toISOString(),
    remitterName: `Remitter ${index + 1}`,
    beneficiaryName: `Beneficiary ${index + 1}`,
    beneficiaryIBAN: `PK${String(index).padStart(22, '0')}`,
    beneficiaryBankName: payoutType === 'IBFT' ? ['Meezan Bank', 'HBL', 'UBL', 'Allied Bank', 'Bank Alfalah', 'MCB', 'Faysal Bank', 'Askari Bank'][index % 8] : undefined,
    railRefs: {
      RRN: lifecycle === 'SUCCESS' ? `RRN${String(index).padStart(12, '0')}` : undefined,
      STAN: lifecycle === 'SUCCESS' ? `${String(index).padStart(6, '0')}` : undefined,
      RaastMsgId: payoutType === 'IBFT' && lifecycle === 'SUCCESS' ? `RAAST${index}` : undefined,
      UETR: payoutType === 'IBFT' && lifecycle === 'SUCCESS' ? `UETR-${index}` : undefined
    },
    ePRCNo: lifecycle === 'SUCCESS' ? `EPRC-${index}` : undefined,
    ePRCDate: lifecycle === 'SUCCESS' ? new Date(baseTime).toISOString() : undefined,
    stateHistory,
    amendAllowed,
    cancelAllowed,
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    amendments: amendAllowed && index % 7 === 0 ? [{
      id: `AMD-${index}-1`,
      timestamp: new Date(baseTime - 1800000).toISOString(),
      status: 'APPROVED' as const,
      reason: 'Beneficiary IBAN correction requested by MTO',
      changes: [{
        field: 'Beneficiary IBAN',
        oldValue: `PK${String(index + 99).padStart(22, '0')}`,
        newValue: `PK${String(index).padStart(22, '0')}`
      }, {
        field: 'Beneficiary Name',
        oldValue: `Beneficiary Old ${index + 1}`,
        newValue: `Beneficiary ${index + 1}`
      }]
    }] : index % 11 === 0 && amendAllowed ? [{
      id: `AMD-${index}-1`,
      timestamp: new Date(baseTime - 2400000).toISOString(),
      status: 'REJECTED' as const,
      reason: 'Amount correction - wrong FCY amount entered',
      changes: [{
        field: 'Amount FCY',
        oldValue: `${(500 + index * 10 + 50).toFixed(2)}`,
        newValue: `${(500 + index * 10).toFixed(2)}`
      }, {
        field: 'Amount PKR',
        oldValue: `${((500 + index * 10 + 50) * 279).toLocaleString()}`,
        newValue: `${((500 + index * 10) * 279).toLocaleString()}`
      }]
    }, {
      id: `AMD-${index}-2`,
      timestamp: new Date(baseTime - 1200000).toISOString(),
      status: 'PENDING' as const,
      reason: 'Re-submitted with corrected amount after rejection',
      changes: [{
        field: 'Amount FCY',
        oldValue: `${(500 + index * 10 + 50).toFixed(2)}`,
        newValue: `${(500 + index * 10).toFixed(2)}`
      }]
    }] : undefined
  } as any;
});
interface TransactionsTableProps {
  searchQuery: string;
}
export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  searchQuery
}) => {
  const {
    showToast
  } = useToast();
  const [selectedRows, setSelectedRows] = useState<Transaction[]>([]);
  const [filters, setFilters] = useState({
    search: searchQuery || '',
    payoutType: [] as PayoutType[],
    region: [] as string[],
    transactionCategory: [] as string[],
    lifecycle: [] as LifecycleStatus[],
    currentState: [] as CurrentState[],
    corridor: [] as string[],
    purposeCode: [] as string[],
    agentCode: [] as string[],
    dateFrom: '',
    dateTo: '',
    amountFcyMin: '',
    amountFcyMax: '',
    amountPkrMin: '',
    amountPkrMax: '',
    mtoRef: '',
    txnId: '',
    branchCode: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>(MOCK_TRANSACTIONS);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [rowActions, setRowActions] = useState<RowAction[]>([]);
  const [viewTransaction, setViewTransaction] = useState(false);
  const [repushedTransactions, setRepushedTransactions] = useState<Transaction[]>([]);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [selectedAmendTransaction, setSelectedAmendTransaction] = useState<Transaction | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState<Transaction | null>(null);
  // Subscribe to repushed transactions
  useEffect(() => {
    const updateRepushedTransactions = () => {
      const repushed = transactionStore.getRepushedTransactions();
      setRepushedTransactions(repushed as Transaction[]);
    };
    updateRepushedTransactions();
    const unsubscribe = transactionStore.subscribe(updateRepushedTransactions);
    return unsubscribe;
  }, []);
  // Add demo transactions to the list
  const allTransactions = [...MOCK_TRANSACTIONS, ...repushedTransactions, ...(DEMO_TRANSACTIONS as any[])];
  // Define row actions - View Details only
  const transactionRowActions: RowAction<Transaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewTransaction(row)
  }];
  // Apply filters whenever filters change
  useEffect(() => {
    let filtered = allTransactions;
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(searchLower) || txn.mtoName.toLowerCase().includes(searchLower) || txn.mtoRef.toLowerCase().includes(searchLower) || txn.bankRef.toLowerCase().includes(searchLower) || txn.beneficiaryName.toLowerCase().includes(searchLower) || txn.remitterName.toLowerCase().includes(searchLower) || txn.branchCode.toLowerCase().includes(searchLower) || txn.agentCode.toLowerCase().includes(searchLower));
    }
    // Txn ID filter
    if (filters.txnId) {
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(filters.txnId.toLowerCase()));
    }
    // Branch Code filter
    if (filters.branchCode) {
      filtered = filtered.filter(txn => txn.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase()));
    }
    // Payout Type filter
    if (filters.payoutType.length > 0) {
      filtered = filtered.filter(txn => filters.payoutType.includes(txn.payoutType));
    }
    // Region filter
    if (filters.region.length > 0) {
      filtered = filtered.filter(txn => filters.region.includes(txn.region));
    }
    // Transaction Category filter
    if (filters.transactionCategory.length > 0) {
      filtered = filtered.filter(txn => filters.transactionCategory.includes(txn.transactionCategory));
    }
    // Lifecycle Status filter
    if (filters.lifecycle.length > 0) {
      filtered = filtered.filter(txn => filters.lifecycle.includes(txn.lifecycleStatus));
    }
    // Current State filter
    if (filters.currentState.length > 0) {
      filtered = filtered.filter(txn => filters.currentState.includes(txn.currentState));
    }
    // Corridor filter
    if (filters.corridor.length > 0) {
      filtered = filtered.filter(txn => filters.corridor.includes(txn.corridor));
    }
    // Purpose Code filter
    if (filters.purposeCode.length > 0) {
      filtered = filtered.filter(txn => filters.purposeCode.includes(txn.purposeCode));
    }
    // Agent Code filter
    if (filters.agentCode.length > 0) {
      filtered = filtered.filter(txn => filters.agentCode.includes(txn.agentCode));
    }
    // Amount FCY filter
    if (filters.amountFcyMin || filters.amountFcyMax) {
      filtered = filtered.filter(txn => {
        const amount = parseFloat(txn.amountFCY);
        if (filters.amountFcyMin && amount < parseFloat(filters.amountFcyMin)) return false;
        if (filters.amountFcyMax && amount > parseFloat(filters.amountFcyMax)) return false;
        return true;
      });
    }
    // Amount PKR filter
    if (filters.amountPkrMin || filters.amountPkrMax) {
      filtered = filtered.filter(txn => {
        const amount = parseFloat(txn.amountPKR.replace(/,/g, ''));
        if (filters.amountPkrMin && amount < parseFloat(filters.amountPkrMin)) return false;
        if (filters.amountPkrMax && amount > parseFloat(filters.amountPkrMax)) return false;
        return true;
      });
    }
    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.createdAt);
        return txnDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.createdAt);
        return txnDate <= toDate;
      });
    }
    setFilteredTransactions(filtered);
    // Update filter chips
    const chips: FilterChip[] = [];
    if (filters.txnId) {
      chips.push({
        id: 'txnId',
        label: 'Txn ID',
        value: filters.txnId,
        onDelete: () => setFilters({
          ...filters,
          txnId: ''
        })
      });
    }
    if (filters.branchCode) {
      chips.push({
        id: 'branchCode',
        label: 'Branch Code',
        value: filters.branchCode,
        onDelete: () => setFilters({
          ...filters,
          branchCode: ''
        })
      });
    }
    if (filters.mtoRef) {
      chips.push({
        id: 'mtoRef',
        label: 'MTO',
        value: filters.mtoRef,
        onDelete: () => setFilters({
          ...filters,
          mtoRef: ''
        })
      });
    }
    if (filters.payoutType.length > 0) {
      chips.push({
        id: 'payoutType',
        label: 'Type',
        value: filters.payoutType.join(', '),
        onDelete: () => setFilters({
          ...filters,
          payoutType: []
        })
      });
    }
    if (filters.region.length > 0) {
      chips.push({
        id: 'region',
        label: 'Region',
        value: filters.region.join(', '),
        onDelete: () => setFilters({
          ...filters,
          region: []
        })
      });
    }
    if (filters.transactionCategory.length > 0) {
      chips.push({
        id: 'transactionCategory',
        label: 'Category',
        value: filters.transactionCategory.join(', '),
        onDelete: () => setFilters({
          ...filters,
          transactionCategory: []
        })
      });
    }
    if (filters.lifecycle.length > 0) {
      chips.push({
        id: 'lifecycle',
        label: 'Status',
        value: filters.lifecycle.map(s => s.replace(/_/g, ' ')).join(', '),
        onDelete: () => setFilters({
          ...filters,
          lifecycle: []
        })
      });
    }
    if (filters.currentState.length > 0) {
      chips.push({
        id: 'currentState',
        label: 'State',
        value: filters.currentState.map(s => s.replace(/_/g, ' ')).join(', '),
        onDelete: () => setFilters({
          ...filters,
          currentState: []
        })
      });
    }
    if (filters.amountFcyMin || filters.amountFcyMax) {
      chips.push({
        id: 'amountFcy',
        label: 'Amount FCY',
        value: filters.amountFcyMin && filters.amountFcyMax ? `${filters.amountFcyMin} - ${filters.amountFcyMax}` : filters.amountFcyMin ? `≥ ${filters.amountFcyMin}` : `≤ ${filters.amountFcyMax}`,
        onDelete: () => setFilters({
          ...filters,
          amountFcyMin: '',
          amountFcyMax: ''
        })
      });
    }
    if (filters.amountPkrMin || filters.amountPkrMax) {
      chips.push({
        id: 'amountPkr',
        label: 'Amount PKR',
        value: filters.amountPkrMin && filters.amountPkrMax ? `${filters.amountPkrMin} - ${filters.amountPkrMax}` : filters.amountPkrMin ? `≥ ${filters.amountPkrMin}` : `≤ ${filters.amountPkrMax}`,
        onDelete: () => setFilters({
          ...filters,
          amountPkrMin: '',
          amountPkrMax: ''
        })
      });
    }
    if (filters.dateFrom) {
      chips.push({
        id: 'dateFrom',
        label: 'From',
        value: new Date(filters.dateFrom).toLocaleDateString(),
        onDelete: () => setFilters({
          ...filters,
          dateFrom: ''
        })
      });
    }
    if (filters.dateTo) {
      chips.push({
        id: 'dateTo',
        label: 'To',
        value: new Date(filters.dateTo).toLocaleDateString(),
        onDelete: () => setFilters({
          ...filters,
          dateTo: ''
        })
      });
    }
    if (filters.agentCode.length > 0) {
      chips.push({
        id: 'agentCode',
        label: 'Agent Code',
        value: filters.agentCode.join(', '),
        onDelete: () => setFilters({
          ...filters,
          agentCode: []
        })
      });
    }
    setFilterChips(chips);
  }, [filters, repushedTransactions]);
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value
    }));
  };
  const handleClearFilters = () => {
    setFilters({
      search: '',
      payoutType: [],
      region: [],
      transactionCategory: [],
      lifecycle: [],
      currentState: [],
      corridor: [],
      purposeCode: [],
      agentCode: [],
      dateFrom: '',
      dateTo: '',
      amountFcyMin: '',
      amountFcyMax: '',
      amountPkrMin: '',
      amountPkrMax: '',
      mtoRef: '',
      txnId: '',
      branchCode: ''
    });
    setFilterChips([]);
    setFilteredTransactions(MOCK_TRANSACTIONS);
  };
  const handleExport = () => {
    if (filteredTransactions.length === 0) {
      showToast('No transactions to export', 'warning');
      return;
    }
    // Simple CSV export
    const headers = ['ID', 'MTO', 'Region', 'Category', 'Type', 'Amount FCY', 'Amount PKR', 'Status', 'State', 'Date'];
    const csvContent = [headers.join(','), ...filteredTransactions.map(t => `"${t.id}","${t.mtoName}","${t.region}","${t.transactionCategory}","${t.payoutType}","${t.amountFCY}","${t.amountPKR}","${t.lifecycleStatus}","${t.currentState}","${t.createdAt}"`)].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Export successful', 'success');
  };
  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDrawerOpen(true);
  };
  const handleAmendClick = (transaction: Transaction) => {
    setSelectedAmendTransaction(transaction);
    setAmendDialogOpen(true);
  };
  const handleCancelClick = (transaction: Transaction) => {
    setSelectedCancelTransaction(transaction);
    setCancelDialogOpen(true);
  };
  const handleBulkApprove = (rows: Transaction[]) => {
    showToast(`Approved ${rows.length} transactions`, 'success');
    setSelectedRows([]);
  };
  const handleBulkReject = (rows: Transaction[]) => {
    showToast(`Rejected ${rows.length} transactions`, 'success');
    setSelectedRows([]);
  };
  const handleBulkCancel = (rows: Transaction[]) => {
    showToast(`Cancelled ${rows.length} transactions`, 'success');
    setSelectedRows([]);
  };
  const bulkActions = [{
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: handleBulkApprove,
    color: 'success' as const
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: handleBulkReject,
    color: 'error' as const
  }, {
    icon: 'lucide:ban',
    label: 'Cancel',
    onClick: handleBulkCancel,
    color: 'warning' as const
  }];
  const confirmRetry = async (reason?: string) => {
    if (!selectedTransaction) return;
    setIsRetrying(true);
    setRetryError(null);
    try {
      // Retry logic here
      await new Promise(resolve => setTimeout(resolve, 2000));
      setRetryDialogOpen(false);
      setTransactionError(null);
      setIsLoadingTransaction(false);
    } catch (error) {
      setRetryError('Failed to retry transaction');
      setIsRetrying(false);
    }
  };
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  const columns: Column<Transaction>[] = [{
    id: 'id',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1142">
          {row.id}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.txnId} onChange={e => setFilters({
      ...filters,
      txnId: e.target.value
    })} sx={{
      width: '100%',
      minWidth: 120
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1144">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1145" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1143" />
  }, {
    id: 'agentCode',
    label: 'Agent Code',
    renderCell: row => <Chip label={row.agentCode || ''} size="small" sx={{
      backgroundColor: 'rgba(0, 82, 204, 0.1)',
      color: '#0052CC',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.agentCode ? 'visible' : 'hidden'
    }} data-id="element-1146" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1147">
          <Select multiple value={filters.agentCode} onChange={e => setFilters({
        ...filters,
        agentCode: e.target.value as string[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-1148">
            {VALID_SUB_AGENTS.map(code => <MenuItem key={code} value={code} data-id="element-1149">
                {code}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || ''} size="small" sx={{
      backgroundColor: 'rgba(6, 182, 212, 0.1)',
      color: '#06B6D4',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.branchCode ? 'visible' : 'hidden'
    }} data-id="element-1150" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1152">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1153" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1151" />
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : row.payoutType === 'IBFT' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1154" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1155">
          <Select multiple value={filters.payoutType} onChange={e => setFilters({
        ...filters,
        payoutType: e.target.value as PayoutType[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-1156">
            <MenuItem value="FT" data-id="element-1157">FT</MenuItem>
            <MenuItem value="IBFT" data-id="element-1158">IBFT</MenuItem>
            <MenuItem value="OTC" data-id="element-1159">OTC</MenuItem>
            <MenuItem value="P2P" data-id="element-1160">P2P</MenuItem>
            <MenuItem value="B2B" data-id="element-1161">B2B</MenuItem>
            <MenuItem value="B2C" data-id="element-1162">B2C</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'region',
    label: 'Region',
    renderCell: row => <Chip label={row.region} size="small" sx={{
      backgroundColor: '#F0F3F7',
      color: '#1F2937',
      fontWeight: 500,
      fontSize: '0.75rem'
    }} data-id="element-1163" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1164">
          <Select multiple displayEmpty value={filters.region} onChange={e => setFilters({
        ...filters,
        region: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
      })} renderValue={selected => {
        if (selected.length === 0) {
          return <Typography color="text.secondary" data-id="element-1166">All Regions</Typography>;
        }
        return selected.join(', ');
      }} sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1165">
            <MenuItem value="Ghana" data-id="element-1167">Ghana</MenuItem>
            <MenuItem value="Nigeria" data-id="element-1168">Nigeria</MenuItem>
            <MenuItem value="Ivory Coast" data-id="element-1169">Ivory Coast</MenuItem>
            <MenuItem value="Senegal" data-id="element-1170">Senegal</MenuItem>
            <MenuItem value="Other" data-id="element-1171">Other</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'transactionCategory',
    label: 'Category',
    renderCell: row => <Typography variant="body2" fontWeight={500} data-id="element-1172">
          {row.transactionCategory}
        </Typography>,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1173">
          <Select multiple displayEmpty value={filters.transactionCategory} onChange={e => setFilters({
        ...filters,
        transactionCategory: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
      })} renderValue={selected => {
        if (selected.length === 0) {
          return <Typography color="text.secondary" data-id="element-1175">All Categories</Typography>;
        }
        return selected.join(', ');
      }} sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1174">
            <MenuItem value="P2P" data-id="element-1176">P2P</MenuItem>
            <MenuItem value="B2B" data-id="element-1177">B2B</MenuItem>
            <MenuItem value="B2C" data-id="element-1178">B2C</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'beneficiaryBankName',
    label: 'Beneficiary Bank',
    renderCell: row => row.payoutType === 'IBFT' && row.beneficiaryBankName ? <Chip label={row.beneficiaryBankName} size="small" sx={{
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#059669',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1179" /> : <Typography variant="body2" sx={{
      color: '#CBD5E1',
      fontSize: '0.8rem'
    }} data-id="element-1180">
            —
          </Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount FCY',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 100
    }} data-id="element-1181">
          {row.currencyFCY} {row.amountFCY}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-1182">
          <TextField size="small" placeholder="Min" value={filters.amountFcyMin} onChange={e => setFilters({
        ...filters,
        amountFcyMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-1183" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-1184" />
          <TextField size="small" placeholder="Max" value={filters.amountFcyMax} onChange={e => setFilters({
        ...filters,
        amountFcyMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-1185" />
        </Box>
  }, {
    id: 'amountPKR',
    label: 'Amount PKR',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 120
    }} data-id="element-1186">
          PKR {row.amountPKR}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-1187">
          <TextField size="small" placeholder="Min" value={filters.amountPkrMin} onChange={e => setFilters({
        ...filters,
        amountPkrMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-1188" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-1189" />
          <TextField size="small" placeholder="Max" value={filters.amountPkrMax} onChange={e => setFilters({
        ...filters,
        amountPkrMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-1190" />
        </Box>
  }, {
    id: 'currentState',
    label: 'Current State',
    renderCell: row => {
      let color = '#6B7280';
      let bgColor = '#F3F5F7';
      if (row.lifecycleStatus === 'SUCCESS') {
        color = '#FFFFFF';
        bgColor = '#0B7923';
      } else if (row.lifecycleStatus === 'FAILED') {
        color = '#FFFFFF';
        bgColor = '#AE2A19';
      } else {
        color = '#FFFFFF';
        bgColor = '#0052CC';
      }
      return <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: bgColor,
        color: color,
        fontWeight: 600,
        fontSize: '0.75rem'
      }} data-id="element-1191" />;
    },
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1192">
          <Select multiple value={filters.currentState} onChange={e => setFilters({
        ...filters,
        currentState: e.target.value as CurrentState[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.map(s => s.replace(/_/g, ' ')).join(', ')} data-id="element-1193">
            <MenuItem value="SCREENING" data-id="element-1194">SCREENING</MenuItem>
            <MenuItem value="TITLE_FETCH" data-id="element-1195">TITLE FETCH</MenuItem>
            <MenuItem value="BALANCE_CHECK" data-id="element-1196">BALANCE CHECK</MenuItem>
            <MenuItem value="FT_POST" data-id="element-1197">FT POST</MenuItem>
            <MenuItem value="RAAST_TITLE_FETCH" data-id="element-1198">RAAST TITLE FETCH</MenuItem>
            <MenuItem value="RAAST_POST" data-id="element-1199">RAAST POST</MenuItem>
            <MenuItem value="SUCCESS" data-id="element-1200">SUCCESS</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'lifecycleStatus',
    label: 'Status',
    renderCell: row => {
      let color = '#6B7280';
      let bgColor = '#F3F5F7';
      switch (row.lifecycleStatus) {
        case 'SUCCESS':
          color = '#FFFFFF';
          bgColor = '#0B7923';
          break;
        case 'IN_PROGRESS':
          color = '#FFFFFF';
          bgColor = '#0052CC';
          break;
        case 'FAILED':
          color = '#FFFFFF';
          bgColor = '#AE2A19';
          break;
        case 'IN_REVIEW':
        case 'PENDING_INQUIRY':
          color = '#FFFFFF';
          bgColor = '#B38600';
          break;
      }
      return <Chip label={row.lifecycleStatus.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: bgColor,
        color: color,
        fontWeight: 600,
        fontSize: '0.75rem'
      }} data-id="element-1201" />;
    },
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1202">
          <Select multiple value={filters.lifecycle} onChange={e => setFilters({
        ...filters,
        lifecycle: e.target.value as LifecycleStatus[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.map(s => s.replace(/_/g, ' ')).join(', ')} data-id="element-1203">
            <MenuItem value="IN_PROGRESS" data-id="element-1204">IN PROGRESS</MenuItem>
            <MenuItem value="SUCCESS" data-id="element-1205">SUCCESS</MenuItem>
            <MenuItem value="FAILED" data-id="element-1206">FAILED</MenuItem>
            <MenuItem value="IN_REVIEW" data-id="element-1207">IN REVIEW</MenuItem>
            <MenuItem value="PENDING_INQUIRY" data-id="element-1208">PENDING INQUIRY</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'transactionStatus',
    label: 'Transaction Status',
    renderCell: row => {
      // Derive transaction status from lifecycle
      let transactionStatus = 'PENDING';
      if (row.lifecycleStatus === 'SUCCESS') {
        transactionStatus = 'FUNDS_TRANSFERRED';
      } else if (row.lifecycleStatus === 'FAILED') {
        transactionStatus = 'FAILED';
      } else if (row.lifecycleStatus === 'IN_REVIEW') {
        transactionStatus = 'UNDER_REVIEW';
      }
      return <Chip label={transactionStatus.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1209" />;
    }
  }, {
    id: 'systemStatus',
    label: 'Status',
    renderCell: row => {
      // Derive system status from current state and lifecycle
      let systemStatus = 'IN_PROGRESS';
      if (row.lifecycleStatus === 'SUCCESS') {
        systemStatus = 'COMPLETED';
      } else if (row.lifecycleStatus === 'FAILED') {
        systemStatus = 'FAILED';
      } else if (row.currentState === 'SCREENING') {
        systemStatus = 'IN_PROGRESS (Screening)';
      }
      const colors = {
        IN_PROGRESS: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        'IN_PROGRESS (Screening)': {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        COMPLETED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        }
      };
      const style = colors[systemStatus as keyof typeof colors] || colors['IN_PROGRESS'];
      return <Chip label={systemStatus} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1210" />;
    }
  }, {
    id: 'createdAt',
    label: 'Created',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1211">
          {new Date(row.createdAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-1212">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-1213">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1214" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1215" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-1216">
              {dateRangeError}
            </Typography>}
        </Box>
  }, {
    id: 'updatedAt',
    label: 'Last Update',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1217">
          {new Date(row.updatedAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      height: 36
    }} data-id="element-1218" />
  }];
  return <>
      <DataTable title="Transactions" data={filteredTransactions} columns={columns} rowActions={transactionRowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} selectable={true} selectedRows={selectedRows} onSelectionChange={setSelectedRows} bulkActions={bulkActions} initialVisibleColumns={['id', 'region', 'transactionCategory', 'payoutType', 'amountFCY', 'amountPKR', 'currentState', 'lifecycleStatus', 'createdAt']} onExport={handleExport} emptyStateMessage="No transactions found" onRowClick={row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }} data-id="element-1219" />

      {selectedTransaction && <TransactionDetailsDrawer open={drawerOpen} onClose={() => {
      setDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={selectedTransaction} isLoading={isLoadingTransaction} error={transactionError} onRetry={() => handleViewTransaction(selectedTransaction)} data-id="element-1220" />}

      {selectedAmendTransaction && <AmendTransactionDialog open={amendDialogOpen} onClose={() => {
      setAmendDialogOpen(false);
      setSelectedAmendTransaction(null);
    }} onConfirm={handleAmendConfirm} transaction={selectedAmendTransaction} data-id="element-1221" />}

      {selectedCancelTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedCancelTransaction(null);
    }} onConfirm={handleCancelConfirm} transaction={selectedCancelTransaction} data-id="element-1222" />}

      {selectedTransaction && <ConfirmDialog open={retryDialogOpen} title="Retry Transaction" message={`Are you sure you want to retry transaction ${selectedTransaction.id}?`} confirmLabel="Retry" showReason={true} reasonLabel="Reason for retry (optional)" reasonPlaceholder="Enter reason for retrying this transaction..." onConfirm={confirmRetry} onCancel={() => setRetryDialogOpen(false)} isLoading={isRetrying} error={retryError} data-id="element-1223" />}
    </>;
};
export default TransactionsTable;